<template>
  <f7-page name="group-financial-setup">
    <f7-navbar :title="$t('groupFinancialSetup.title')" back-link no-shadow>
      <f7-nav-right>
        <f7-link v-if="!loading && canManage" fill small @click="refresh">
          <f7-icon f7="arrow_clockwise" size="14" />
          <!-- {{ $t('common.refresh') }} -->
        </f7-link>
      </f7-nav-right>
    </f7-navbar>
    <AppLayout>
      <template #main>
        <f7-block v-if="loading" class="text-align-center">
          <f7-preloader />
          <p class="text-color-gray">{{ $t('common.loading') }}</p>
        </f7-block>

        <template v-else>
          <div class="group-hero bg-surface-container-low overflow-hidden">
            <div class="relative width-100 h-44 overflow-hidden">
              <div class="width-100 h-full display-flex align-items-center justify-content-center bg-gradient-to-br from-primary/15 via-surface-container to-secondary-container/20">
                <f7-icon material="account_balance" :size="40" class="text-primary" />
              </div>
              <div class="absolute inset-0 bg-gradient-to-t from-primary/90 via-primary/35 to-transparent"></div>
            </div>
            <div class="px-4 pt-3 pb-4">
              <h1 class="font-headline-sm text-headline-sm text-on-surface font-bold tracking-tight leading-tight">{{ group?.name || 'Group' }}</h1>
              <p class="font-body-sm text-body-sm text-on-surface-variant font-medium mt-0.5">
                {{ $t('groupFinancialSetup.setupSubtitle') }}
              </p>
            </div>
          </div>

          <f7-block strong inset class="progress-card">
            <div class="display-flex justify-content-space-between align-items-center mb-1">
              <span class="font-label-md text-on-surface-variant font-semibold">{{ $t('groupFinancialSetup.progressTitle') }}</span>
              <span class="font-label-md font-bold" :class="progressPct === 100 ? 'text-green' : 'text-primary'">{{ progressLabel }}</span>
            </div>
            <div class="progress-track">
              <div class="progress-fill" :style="{ width: progressPct + '%' }"></div>
            </div>
            <p v-if="allDone" class="mt-2 mb-0 font-body-sm text-green font-semibold display-flex align-items-center gap-1">
              <f7-icon f7="checkmark_seal_fill" size="16" color="green" />
              {{ $t('groupFinancialSetup.fullyVerifiedDesc') }}
            </p>
          </f7-block>

          <f7-block-title>{{ $t('groupFinancialSetup.financialFeatures') }}</f7-block-title>
          <f7-list inset strong dividers outline media-list>
            <f7-list-item
              v-for="feature in financialFeatures"
              :key="feature.key"
              :title="feature.label"
              :subtitle="feature.description"
            >
              <template #media>
                <f7-icon :f7="feature.icon" :color="feature.color" size="28" />
              </template>
              <template #after>
                <f7-badge :color="feature.badgeColor">{{ feature.badgeLabel }}</f7-badge>
              </template>
              <template #footer>
                <div v-if="feature.missingDependencies.length" class="prereq-line display-flex align-items-center gap-1 text-color-gray font-label-sm">
                  <f7-icon f7="lock_fill" size="12" />
                  <span>{{ $t('groupFinancialSetup.needsPrerequisites') }}</span>
                  <span class="font-semibold">{{ feature.missingDependenciesText }}</span>
                </div>
                <div v-else-if="!financialPrereqsMet.value" class="prereq-line display-flex align-items-center gap-1 text-color-gray font-label-sm">
                  <f7-icon f7="lock_fill" size="12" />
                  <span>{{ $t('groupFinancialSetup.payoutSetup') }} {{ $t('groupFinancialSetup.verification') }}</span>
                </div>
                <div v-if="canManage" class="display-flex align-items-center gap-2 flex-wrap mt-1">
                  <f7-button v-if="feature.enabled" small raised :disabled="busyKey === feature.key" outline round @click="disableFeature(feature.key)">
                    <f7-icon f7="minus_circle" size="14" />
                    {{ $t('groupFinancialSetup.disable') }}
                  </f7-button>
                  <f7-button v-if="feature.enabled === false" small fill raised round :disabled="busyKey === feature.key || !financialPrereqsMet.value" @click="enableFeature(feature.key)">
                    <f7-icon f7="plus_circle_fill" size="14" />
                    {{ $t('groupFinancialSetup.enable') }}
                  </f7-button>
                  <f7-button v-if="feature.missingDependencies.length || !financialPrereqsMet.value" small raised outline round :disabled="busyKey === feature.key" @click="checkRequirements(feature.key)">
                    <f7-icon f7="exclamationmark_shield_fill" size="14" />
                    {{ $t('groupFinancialSetup.checkRequirements') }}
                  </f7-button>
                </div>
                <div v-if="!canManage" class="display-flex align-items-center gap-1 mt-1 text-color-gray font-label-sm">
                  <f7-icon f7="lock_fill" size="12" />
                  <span>{{ $t('groupFinancialSetup.adminOnly') }}</span>
                </div>
              </template>
            </f7-list-item>
          </f7-list>

          <f7-block-title>{{ $t('groupFinancialSetup.payoutSetup') }}</f7-block-title>
          <f7-list inset strong dividers outline>
            <f7-list-item :title="$t('groupFinancialSetup.payoutConfig')" :subtitle="$t('groupFinancialSetup.payoutConfigDesc')">
              <template #after>
                <f7-badge :color="payoutStatusColor">{{ payoutStatusLabel }}</f7-badge>
              </template>
              <template #footer>
                <div v-if="payoutSetup" class="payout-details font-label-sm text-on-surface-variant">
                  <div class="display-flex justify-content-space-between gap-2 py-0.5">
                    <span>{{ $t('groupFinancialSetup.beneficiaryType') }}</span>
                    <span class="font-semibold text-on-surface">{{ beneficiaryTypeLabel }}</span>
                  </div>
                  <div v-if="recipientDisplayName" class="display-flex justify-content-space-between gap-2 py-0.5">
                    <span class="text-on-surface-variant">{{ recipientNameLabel }}</span>
                    <span class="font-semibold text-on-surface">{{ recipientDisplayName }}</span>
                  </div>
                  <div class="display-flex justify-content-space-between gap-2 py-0.5">
                    <span>{{ $t('groupFinancialSetup.kycStatus.status') }}</span>
                    <span class="font-semibold" :class="'text-' + kycStatusColor">
                      {{ kycStatusLabel }}
                    </span>
                  </div>
                </div>
                <div v-if="canManage" class="display-flex align-items-center gap-2 flex-wrap mt-1">
                  <f7-button small fill round raised @click="goPayoutConfig">
                    <f7-icon f7="arrow_right_arrow_left" size="14" />
                    {{ $t('groupFinancialSetup.configure') }}
                  </f7-button>
                </div>
              </template>
            </f7-list-item>
          </f7-list>

          <f7-block-title>{{ $t('groupFinancialSetup.verification') }}</f7-block-title>
          <f7-list inset strong dividers outline>
            <f7-list-item :title="$t('groupFinancialSetup.verifyRecipient')" :subtitle="$t('groupFinancialSetup.verifyRecipientDesc')">
              <template #after>
                <f7-icon v-if="recipientVerified" f7="checkmark_circle_fill" :color="'green'" size="24" />
                <f7-icon v-else f7="hourglass" :color="'orange'" size="24" />
              </template>
              <template #footer>
                <f7-button v-if="canManage && !recipientVerified" small outline raised round @click="goVerifyRecipient">
                  <f7-icon f7="person_crop_circle_badge_checkmark" size="14" />
                  {{ $t('groupFinancialSetup.startVerification') }}
                </f7-button>
              </template>
            </f7-list-item>
            <f7-list-item :title="$t('groupFinancialSetup.verifyGroup')" :subtitle="$t('groupFinancialSetup.verifyGroupDesc')">
              <template #after>
                <f7-badge :color="groupVerificationColor">{{$t(groupVerificationLabel)}}</f7-badge>
              </template>
            </f7-list-item>
          </f7-list>

          <f7-block-title>{{ $t('groupFinancialSetup.nextSteps') }}</f7-block-title>
          <f7-list inset strong dividers outline>
            <f7-list-item
              v-for="step in steps"
              :key="step.key"
              :title="step.label"
              :subtitle="step.description"
            >
              <template #media>
                <div class="step-icon" :class="step.done ? 'step-done' : 'step-pending'">
                  <f7-icon v-if="step.done" f7="checkmark_circle_fill" size="22" color="green" />
                  <f7-icon v-else f7="circle" size="22" color="gray" />
                </div>
              </template>
            </f7-list-item>
          </f7-list>

          <f7-block strong inset class="info-card">
            <p class="font-label-md font-bold m-0">{{ $t('groupFinancialSetup.info') }}</p>
            <ul class="info-list m-0">
              <li>{{ $t('groupFinancialSetup.infoText1') }}</li>
              <li>{{ $t('groupFinancialSetup.infoText2') }}</li>
              <li>{{ $t('groupFinancialSetup.infoText3') }}</li>
            </ul>
          </f7-block>
        </template>
      </template>
    </AppLayout>
  </f7-page>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { f7 } from 'framework7-vue'
import { storeToRefs } from 'pinia'
import AppLayout from '../components/common/AppLayout.vue'
import { useGroupsStore } from '../js/pinia-stores/group.js'
import { useKeycloakStore } from '../js/pinia-stores/keycloak.js'
import { useMembersStore } from '../js/pinia-stores/members.js'
import { useKycStore } from '../js/pinia-stores/kyc.js'
import { FINANCIAL_FEATURE_META as FEATURE_META_IMPORT, FEATURE_DEPENDENCIES } from '../constants/group-financial-features.js'
import i18n from '../js/i18n.js'
const { t } = i18n.global

const props = defineProps({
  f7route: Object,
  f7router: Object,
})

const groupsStore = useGroupsStore()
const keycloakStore = useKeycloakStore()
const membersStore = useMembersStore()
const kycStore = useKycStore()
const { kycStatus, isVerified: kycIsVerified } = storeToRefs(kycStore)

const group = computed(() => groupsStore.activeGroup)
const loading = ref(true)
const busyKey = ref(null)

const groupId = computed(() => props.f7route?.params?.groupId)

const canManage = computed(() => {
  const viewer = membersStore.members.find(
    (m) => m.userId === keycloakStore.subject,
  )
  return viewer?.role === 'OWNER' || viewer?.role === 'ADMIN'
})

const enabledFeatures = computed(() => group.value?.features || [])
const totalFeatures = computed(() => Object.keys(FEATURE_META_IMPORT).length)
const enabledCount = computed(() =>
  enabledFeatures.value.filter((f) => FEATURE_META_IMPORT[f]).length,
)
const progressPct = computed(() =>
  totalFeatures.value
    ? Math.round((enabledCount.value / totalFeatures.value) * 100)
    : 0,
)
const progressLabel = computed(() =>
  `${enabledCount.value}/${totalFeatures.value}`,
)
const allDone = computed(() => enabledCount.value === totalFeatures.value)

const financialFeatures = computed(() =>
  Object.keys(FEATURE_META_IMPORT).map((key) => {
    const meta = FEATURE_META_IMPORT[key]
    const deps = FEATURE_DEPENDENCIES[key] || []
    const missing = deps.filter((d) => !enabledFeatures.value.includes(d))
    const isEnabled = enabledFeatures.value.includes(key)
    return {
      key,
      icon: meta.icon,
      color: getFeatureColor(key),
      enabled: isEnabled,
      missingDependencies: missing,
      missingDependenciesText: missing
        .map((d) => t(`groupFinancialSetup.features.${d}.label`))
        .join(', '),
      badgeLabel: isEnabled
        ? t('groupFinancialSetup.enabled')
        : missing.length
          ? t('groupFinancialSetup.needsPrerequisites')
          : t('groupFinancialSetup.disabled'),
      badgeColor: isEnabled ? 'green' : missing.length ? 'orange' : 'gray',
      label: t(`groupFinancialSetup.features.${key}.label`),
      description: t(`groupFinancialSetup.features.${key}.description`),
    }
  }),
)

const payoutSetup = computed(() => group.value?.payoutSetup || null)
const beneficiaryType = computed(
  () => payoutSetup.value?.beneficiaryType || 'INDIVIDUAL',
)
const beneficiaryTypeLabel = computed(() =>
  t(
    'groupFinancialSetup.beneficiaryType.' +
      (beneficiaryType.value === 'ORGANIZATION' ? 'organization' : 'individual'),
  ),
)
const recipientNameLabel = computed(() =>
  beneficiaryType.value === 'ORGANIZATION'
    ? t('groupFinancialSetup.organizationName')
    : t('groupFinancialSetup.recipientName'),
)
const recipientDisplayName = computed(() => {
  const setup = payoutSetup.value
  if (!setup) return ''
  return (
    setup.recipientName ||
    setup.organizationName ||
    setup.legalOrganizationName ||
    setup.fullName ||
    ''
  )
})

const recipientVerified = computed(
  () => kycIsVerified.value || kycStatus.value?.status === 'VERIFIED',
)

const payoutStatusKey = computed(() => {
  const ps = payoutSetup.value
  const configured = ps?.verified === true || ps?.recipientVerified === true
  if (configured && recipientVerified.value) return 'payoutConfigured'
  if (configured) return 'payoutPendingKyc'
  return 'payoutNotConfigured'
})
const payoutStatusLabel = computed(() =>
  t(`groupFinancialSetup.${payoutStatusKey.value}`),
)
const payoutStatusColor = computed(() => {
  if (payoutStatusKey.value === 'payoutConfigured') return 'green'
  if (payoutStatusKey.value === 'payoutPendingKyc') return 'orange'
  return 'gray'
})

const kycRawStatus = computed(() => kycStatus.value?.status || 'NOT_STARTED')
const kycStatusLabel = computed(() =>
  t(`groupFinancialSetup.kycStatus.${kycRawStatus.value}`),
)
const kycStatusColor = computed(() => {
  if (kycRawStatus.value === 'VERIFIED') return 'green'
  if (kycRawStatus.value === 'REJECTED') return 'red'
  if (kycRawStatus.value === 'PENDING') return 'orange'
  return 'gray'
})

const groupVerificationRaw = computed(
  () =>
    group.value?.financialVerificationStatus ||
    group.value?.verificationStatus ||
    'NOT_STARTED',
)
const groupVerificationLabel = computed(() =>
  t(`groupFinancialSetup.kycStatus.${groupVerificationRaw.value}`)
)
const groupVerificationColor = computed(() => {
  if (groupVerificationRaw.value === 'VERIFIED') return 'green'
  if (groupVerificationRaw.value === 'REJECTED') return 'red'
  if (groupVerificationRaw.value === 'PENDING') return 'orange'
  return 'gray'
})

const financialPrereqsMet = computed(() => {
  const ps = payoutSetup.value
  const payoutVerified = ps?.verified === true || ps?.recipientVerified === true
  const finVerificationVerified = groupVerificationRaw.value === 'VERIFIED'
  return payoutVerified && finVerificationVerified
})

const steps = computed(() => {
  const configureDone = recipientVerified.value && !!payoutSetup.value
  const groupDone = groupVerificationRaw.value === 'VERIFIED'
  const featuresDone = allDone.value
  return [
    {
      key: 'configurePayout',
      label: t('groupFinancialSetup.steps.configurePayout'),
      description: t('groupFinancialSetup.steps.configurePayoutDesc'),
      done: configureDone,
    },
    {
      key: 'verifyGroup',
      label: t('groupFinancialSetup.steps.verifyGroup'),
      description: t('groupFinancialSetup.steps.verifyGroupDesc'),
      done: groupDone,
    },
    {
      key: 'enableFeatures',
      label: t('groupFinancialSetup.steps.enableFeatures'),
      description: t('groupFinancialSetup.steps.enableFeaturesDesc'),
      done: featuresDone,
    },
    {
      key: 'allDone',
      label: t('groupFinancialSetup.steps.allDone'),
      description: t('groupFinancialSetup.steps.allDoneDesc'),
      done: configureDone && groupDone && featuresDone,
    },
  ]
})

function getFeatureColor(key) {
  const colors = {
    FUND_COLLECTION: '#185fa5',
    TONTINE_CYCLES: '#a08520',
    GROUP_SAVINGS: '#0e7490',
    SAVE_TO_BUY: '#7a3bb8',
    GROUP_BUY: '#1a6b42',
    MARKETPLACE: '#1a6b42',
    PAYMENT_LINKS: '#6b46c1',
    SUBSCRIPTIONS: '#c0392b',
    AD_MONETIZATION: '#e67e22',
  }
  return colors[key] || '#666'
}

function featureLabel(key) {
  return t(`groupFinancialSetup.features.${key}.label`)
}

async function enableFeature(key) {
  if (busyKey.value || !groupId.value) return
  busyKey.value = key
  f7.preloader.show()
  try {
    await groupsStore.enableFinancialFeature(groupId.value, key)
    f7.toast.show({
      text: t('groupFinancialSetup.featureEnabled', { feature: featureLabel(key) }),
      closeTimeout: 1800,
      icon: '<img src="/512x512.svg"/>'
    })
  } catch (e) {
    f7.dialog.alert(e?.response?.data?.message || t('groupFinancialSetup.enableFailed'))
  } finally {
    busyKey.value = null
    f7.preloader.hide()
  }
}

async function disableFeature(key) {
  if (busyKey.value || !groupId.value) return
  busyKey.value = key
  f7.preloader.show()
  try {
    await groupsStore.disableFinancialFeature(groupId.value, key)
    f7.toast.show({
      text: t('groupFinancialSetup.featureDisabled', { feature: featureLabel(key) }),
      closeTimeout: 1800,
      icon: '<img src="/512x512.svg"/>'
    })
  } catch (e) {
    f7.dialog.alert(e?.response?.data?.message || t('groupFinancialSetup.disableFailed'))
  } finally {
    busyKey.value = null
    f7.preloader.hide()
  }
}

async function checkRequirements(key) {
  if (busyKey.value || !groupId.value) return
  busyKey.value = key
  f7.preloader.show()
  try {
    const data = await groupsStore.assessFinancialReadiness(groupId.value, key)
    const blockers = data?.blockers || []
    const warnings = data?.warnings || []
    const lines = []
    if (Array.isArray(blockers)) lines.push(...blockers)
    if (Array.isArray(warnings)) lines.push(...warnings)
    let text = lines.length ? lines.join('\n') : ''
    if (data?.ready) {
      if (text) text += '\n'
      text += t('groupFinancialSetup.requirementsMet')
    } else if (!text.trim()) {
      text = t('groupFinancialSetup.requirementsMissing')
    }
    f7.dialog.alert(text, featureLabel(key))
  } catch (e) {
    f7.dialog.alert(
      e?.response?.data?.message || t('groupFinancialSetup.requirementsFailed'),
      featureLabel(key),
    )
  } finally {
    busyKey.value = null
    f7.preloader.hide()
  }
}

function goPayoutConfig() {
  const router = f7.views?.main?.router
  router?.navigate(`/groups/${groupId.value}/payout-recipient-setup`)
}

function goVerifyRecipient() {
  const router = f7.views?.main?.router
  const type = beneficiaryType.value === 'ORGANIZATION' ? 'organization' : 'individual'
  router?.navigate(`/kyc/${type}/${groupId.value}`)
}

function refresh() {
  loading.value = true
  Promise.all([
    groupsStore.fetchGroupDetail(groupId.value),
    kycStore.fetchKycStatus(groupId.value),
  ])
    .catch((e) => console.error('refresh financial setup failed', e))
    .finally(() => {
      loading.value = false
    })
}

onMounted(async () => {
  await Promise.all([
    groupsStore.fetchGroupDetail(groupId.value),
    membersStore.fetchMembers(groupId.value),
  ])
    .catch((e) => console.error('load financial setup failed', e))
  try {
    await kycStore.fetchKycStatus(groupId.value)
  } catch (e) {
    console.warn('No KYC status available yet', e)
  }
  loading.value = false
})
</script>

<style scoped>
.group-hero {
  margin-bottom: 12px;
}

.progress-card {
  margin-top: 0;
}

.progress-track {
  height: 8px;
  border-radius: 4px;
  background: var(--f7-list-item-border-color, #e0e0e0);
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  border-radius: 4px;
  background: var(--f7-theme-color);
  transition: width 0.3s ease;
}

.prereq-line {
  flex-wrap: wrap;
}

.payout-details {
  width: 100%;
}

.text-green {
  color: #1a6b42;
}

.info-card {
  margin-top: 4px;
}

.info-list {
  padding-left: 18px;
  color: var(--f7-on-surface-variant);
  font-size: 13px;
  line-height: 1.5;
  margin-top: 6px;
}

.info-list li {
  margin-bottom: 4px;
}
</style>